import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client.js';

export interface FreeRef {
  url: string;
  label: string;
  /** Set when the reference came from a Whacky Whale lookup — lets the amplifier
   *  use official trait metadata for higher fidelity. Absent for uploads. */
  tokenId?: number | null;
}

/**
 * FreeReferencePicker — the optional "bring a character" step for the no-wallet
 * free gild. A visitor can either look up a Whacky Whale by number (public
 * lookup → its image) or upload their own whale / NFT / character image (anon R2
 * upload). The chosen image URL becomes the free gild's reference, so it runs
 * reference-based (gpt-image-1.5) and preserves the character. Optional — leave
 * it empty for a pure sketch gild.
 */
export function FreeReferencePicker({
  value,
  onChange,
  uploadFn = api.uploadReferenceAnon,
}: {
  value: FreeRef | null;
  onChange: (r: FreeRef | null) => void;
  /** Which upload endpoint to use. Defaults to the anonymous one (free flow);
   *  the signed-in flow passes `api.uploadReference` (authenticated R2 write). */
  uploadFn?: (file: File) => Promise<{ url: string }>;
}) {
  const [mode, setMode] = useState<'whale' | 'upload' | null>(null);
  const [num, setNum] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const whaleMut = useMutation({
    mutationFn: (tokenId: number) => api.whalesLookup(tokenId),
    onSuccess: (r) => {
      onChange({ url: r.whale.imageUrl, label: r.whale.name, tokenId: r.whale.tokenId });
      setMode(null);
    },
  });
  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadFn(file),
    onSuccess: (r, file) => {
      onChange({ url: r.url, label: file.name });
      setMode(null);
    },
  });

  // A chosen reference — show it with a way to clear.
  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-sm border border-accent/30 bg-accent/[0.05] px-3 py-3">
        <img
          src={value.url}
          alt=""
          className="h-14 w-14 shrink-0 rounded-sm object-cover"
          style={{ boxShadow: '0 0 0 1px rgba(201,165,70,.35)' }}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-caption text-ink-soft">{value.label}</p>
          <p className="font-body text-micro text-ink-subtle">
            Gilded as your character
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setMode(null);
          }}
          className="shrink-0 font-body text-micro uppercase tracking-widest text-ink-subtle transition-colors hover:text-accent"
        >
          Remove
        </button>
      </div>
    );
  }

  const busy = whaleMut.isPending || uploadMut.isPending;
  const whaleErr = (whaleMut.error)?.message;
  const uploadErr = (uploadMut.error)?.message;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <ChoiceChip active={mode === 'whale'} onClick={() => setMode(mode === 'whale' ? null : 'whale')}>
          🐋 Whale by number
        </ChoiceChip>
        <ChoiceChip active={mode === 'upload'} onClick={() => setMode(mode === 'upload' ? null : 'upload')}>
          ⬆ Upload an image
        </ChoiceChip>
      </div>

      {mode === 'whale' && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={10000}
              value={num}
              onChange={(e) => setNum(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && num.trim()) whaleMut.mutate(Number(num));
              }}
              placeholder="Whale #, e.g. 1234"
              className="w-40 rounded-sm border border-accent/25 bg-transparent px-3 py-2 font-body text-caption text-ink placeholder:text-ink-subtle focus:border-accent/60 focus:outline-none"
            />
            <button
              type="button"
              disabled={!num.trim() || busy}
              onClick={() => whaleMut.mutate(Number(num))}
              className="rounded-sm border border-accent/40 px-4 py-2 font-body text-caption uppercase tracking-widest text-accent transition-colors hover:bg-accent/10 disabled:opacity-40"
            >
              {whaleMut.isPending ? 'Finding…' : 'Find'}
            </button>
          </div>
          {whaleErr && <p className="font-display italic text-caption text-danger">{whaleErr}</p>}
        </div>
      )}

      {mode === 'upload' && (
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadMut.mutate(f);
            }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="rounded-sm border border-dashed border-accent/35 px-4 py-3 font-body text-caption text-ink-soft transition-colors hover:border-accent/60 disabled:opacity-40"
          >
            {uploadMut.isPending ? 'Uploading…' : 'Choose an image (PNG/JPG/WEBP, up to 6MB)'}
          </button>
          {uploadErr && <p className="font-display italic text-caption text-danger">{uploadErr}</p>}
        </div>
      )}
    </div>
  );
}

function ChoiceChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-sm border px-3 py-2 font-body text-caption transition-colors ' +
        (active
          ? 'border-accent/60 bg-accent/10 text-accent'
          : 'border-accent/20 text-ink-soft hover:border-accent/40')
      }
    >
      {children}
    </button>
  );
}
